import { FC } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'

import { Box, Divider, Typography, alpha, useTheme } from '@mui/material'

import { borderRadiusDefault } from '../../../../themes/theme'
import { MAX_INTENSITY, MIN_INTENSITY, ROW_H, TOOLTIP_WIDTH } from './TimelinePalette'
import type { TooltipPosition, TooltipState } from './useTimelineTooltip'

interface TimelineTooltipProps {
  tooltip: TooltipState
  position: TooltipPosition
}

/**
 * Floating, portal-rendered tooltip overlay for {@link MotivationTimeline}.
 * The tooltip body lays each motivation row out at the same `ROW_H` rhythm
 * as the chart rows so individual swatches/values line up with their
 * corresponding chart row, and a footer (divider + "Second N" title) sits
 * below the rows block. Rendered into `document.body` so it escapes the
 * chart's horizontal scroll container's overflow clipping.
 */
export const TimelineTooltip: FC<TimelineTooltipProps> = ({ tooltip, position }) => {
  const theme = useTheme()
  const { t } = useTranslation('ua-insights')

  return createPortal(
    <Box
      sx={{
        position: 'fixed',
        top: position.top,
        left: position.left,
        width: TOOLTIP_WIDTH,
        pointerEvents: 'none',
        // Slightly translucent so the chart underneath stays faintly visible
        // — gives the tooltip a "floating overlay" feel.
        backgroundColor: alpha(theme.palette.grey[900], 0.9),
        color: 'common.white',
        borderRadius: borderRadiusDefault,
        boxShadow: 6,
        p: 1.5,
        zIndex: theme.zIndex.tooltip,
        backdropFilter: 'blur(2px)',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {tooltip.items.map((item) => {
          const formatted = item.intensity.toFixed(1)
          const clampedIntensity = Math.min(Math.max(item.intensity, MIN_INTENSITY), MAX_INTENSITY)
          // Bar fill is normalised against the 1-5 range so the baseline
          // value 1.0 reads as an empty bar (0%) and 5.0 reads as full
          // (100%), matching how the timeline rows themselves treat
          // intensity 1 as the row baseline.
          const normalized = (clampedIntensity - MIN_INTENSITY) / (MAX_INTENSITY - MIN_INTENSITY)
          const fillPct = normalized * 100
          // Non-linear (4th-root) ramp from 0 → 1 alpha so the lower end of
          // the 1-5 scale picks up colour quickly: intensity 2.0 already
          // lands at ~0.71 alpha while 1.0 is 0 (transparent) and 5.0 is
          // fully saturated.
          const fillAlpha = Math.pow(normalized, 0.25)
          return (
            <Box
              key={item.label}
              sx={{
                display: 'grid',
                // Leading 10px swatch acts as the colour legend so the
                // tooltip mirrors the per-motivation row colours rendered
                // in the timeline above.
                gridTemplateColumns: '10px 1fr 44px 28px',
                alignItems: 'center',
                columnGap: 0.75,
                // Lock the row height to ROW_H so each tooltip row lines up
                // vertically with its corresponding motivation row in the
                // chart.
                height: ROW_H,
              }}
            >
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: item.color }} />
              <Typography
                sx={{
                  fontSize: 12,
                  color: 'common.white',
                  textAlign: 'left',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
              >
                {item.label}
              </Typography>
              <Box
                sx={{
                  height: 6,
                  borderRadius: 3,
                  backgroundColor: alpha(theme.palette.common.white, 0.15),
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    bottom: 0,
                    left: 0,
                    width: `${fillPct}%`,
                    backgroundColor: alpha(item.color, fillAlpha),
                    borderRadius: 3,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: 11,
                  color: 'common.white',
                  textAlign: 'right',
                  fontVariantNumeric: 'tabular-nums',
                }}
              >
                {formatted}
              </Typography>
            </Box>
          )
        })}
      </Box>
      {/* Footer geometry is locked (divider with 8/1/8 my margins + title
          18px line + outer 12px padding-bottom) so it sums to
          TOOLTIP_FOOTER_HEIGHT, which the positioner uses to keep the rows
          block pinned to the chart rows above. */}
      <Divider sx={{ my: 1, borderColor: alpha(theme.palette.common.white, 0.15) }} />
      <Typography sx={{ fontSize: 13, fontWeight: 700, lineHeight: '18px', color: 'common.white' }}>
        {t('motivation_timeline_tooltip_title', { count: tooltip.second })}
      </Typography>
    </Box>,
    document.body
  )
}
